// @flow
import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { AppLink } from '@finect/tabular-core/AppLink';
import { ArrowRight } from '@finect/tabular-icons/arrowRight';
import { maxMediaQueries } from '@finect/tabular-utils/mediaQueries';
import { grid } from '@finect/tabular-utils/grid';
import { getFont } from '@finect/tabular-utils/getFont';
import { fontSmoothing } from '@finect/tabular-utils/fontSmoothing';
import { ChevronDown } from '@finect/tabular-icons/chevronDown';

const Item = styled.div`
  display: flex;
  align-items: flex-start;
  max-width: 980px;
  margin: 0 auto;

  &:not(:first-child) {
    padding-top: 42px;
    border-top: 1px solid ${({ theme }) => theme.colors.ui};
    margin-top: 42px;
  }

  > div:first-child {
    min-width: 30%;
  }

  > div:last-child {
    min-width: 70%;
    padding-left: 48px;
  }

  h2 {
    max-width: ${grid(3)}px;
    ${getFont(2, 'compact')};
    font-weight: 600;
  }

  p {
    max-width: ${grid(7)}px;
    ${getFont(1)};
    ${fontSmoothing};
  }

  ${maxMediaQueries.miniDesktop`
    h2 {
      ${getFont(1, 'compact')};
      font-weight: 700;
    }

    p {
      ${getFont(0)};
    }
  `};

  ${maxMediaQueries.tablet`
    width: 100%;
    max-width: ${grid(5)}px;
    margin: 0 auto;

    h2 {
      max-width: none;
      margin-bottom: 18px;
    }
  
    flex-wrap: wrap;

    > div {
      width: 100%;
    }

    > div:last-child {
      padding-left: 0;
    }
  `};

  ${maxMediaQueries.phablet`
    &:not(:first-child) {
      padding-top: 24px;
      margin-top: 24px;
    }

    &:last-child {
      padding-bottom: 24px;
      border-bottom: 1px solid ${({ theme }) => theme.colors.ui};
    }

    h2 {
      position: relative;
      ${getFont(1, 'compact')};
      font-weight: 700;
      padding-right: 36px;
      margin-bottom: 0;
      cursor: pointer;

      svg {
        display: block;
        position: absolute;
        top: 2px;
        right: 0;
      }
    }

    > div:last-child {
      overflow: hidden;
      max-height: ${({ show }) => (show ? '300px' : 0)};
      transition: max-height 0.25s cubic-bezier(0.49, 0.42, 0.52, 0.98);

      p {
        padding-top: 18px;
      }
    }
  `};
`;

const ItemBasic = styled(Item)`
  > :first-child {
    max-width: 100%;
    cursor: pointer;
  }
`;

const ShowContent = styled.div`
  display: none;

  svg {
    ${({ show }) => show && 'transform: rotate(-180deg);'};
    transition: transform 0.15s ease;
  }

  ${maxMediaQueries.phablet`
    display: block;
    position: absolute;
    top: 0;
    right: 0;
  `};
`;

const KnowMore = styled.div`
  margin-top: 24px;
  color: ${({ theme }) => theme.linkColor};
  text-align: right;

  svg {
    position: relative;
    top: 3px;
    margin-left: 3px;
    transition: transform 0.25s ease;
  }

  ${maxMediaQueries.tablet`
    margin-top: 30px;
  `};

  a {
    display: inline-block;
    padding: 3px 15px;
    border-radius: 30px;
    background-color: ${({ theme }) => theme.colors.uiL};
    color: ${({ theme }) => theme.colors.font};

    &:hover {
      color: ${({ theme }) => theme.linkColor};

      svg {
        transform: translateX(3px);
      }
    }
  }
`;

type Props = {
  title: string,
  linkText: string | null,
  path: string,
  basic?: boolean,
  children: any
};

type State = {
  show: boolean
};

export class FaqItem extends React.Component<Props, State> {
  state = {
    show: false
  };

  toggleContent = () => {
    this.setState(state => ({ show: !state.show }));
  };

  render() {
    const { show } = this.state;
    const { title, path, children, linkText, basic } = this.props;

    if (basic) {
      return (
        <ItemBasic>
          <h2>
            <AppLink href={path}>{title}</AppLink>
          </h2>
        </ItemBasic>
      );
    }

    return (
      <Item show={show}>
        <div>
          <h2 onClick={this.toggleContent}>
            {title}
            <ShowContent show={show}>
              <ChevronDown />
            </ShowContent>
          </h2>
        </div>
        <div>
          {children}
          {path && (
            <KnowMore>
              <Link to={path} data-fi-event="home-asesores">
                {linkText ? linkText : title} <ArrowRight size="18" />
              </Link>
            </KnowMore>
          )}
        </div>
      </Item>
    );
  }
}
